import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { auditKeys } from "@amber/access/ui";
import { POLL_MS, apiGet, apiPost, withQuery } from "@amber/ui";
import {
  appearancesResponseSchema,
  candidatesResponseSchema,
  discoveryStartedSchema,
  draftDeletedSchema,
  duplicatesResponseSchema,
  exportResultSchema,
  identityProposalSchema,
  importPreviewSchema,
  importResultSchema,
  mergePreviewSchema,
  mergeReversedSchema,
  mergeStartedSchema,
  profileCreatedSchema,
  profileHistoryResponseSchema,
  profileOptionsResponseSchema,
  profileReactivatedSchema,
  profileRevisionSchema,
  profileSplitSchema,
  refreshDecidedSchema,
  refreshProposalSchema,
  suggestionDecidedSchema,
  trackingStartedSchema,
  type CreateProfileRequest,
  type EditIdentityRequest,
  type MergeFieldChoices,
  type PlanHistory,
  type ProfileHistoryCategory,
} from "../contracts/index";

export const profileKeys = {
  all: ["profiles"],
  options: ["profiles", "options"],
  roster: ["profiles", "roster"],
  detail: (id: string) => ["profiles", "detail", id] as const,
  plan: (id: string) => ["profiles", "plan", id] as const,
  appearances: (id: string) => ["profiles", "appearances", id] as const,
  candidates: (q: string) => ["profiles", "candidates", q] as const,
  duplicates: (name: string) => ["profiles", "duplicates", name] as const,
  proposal: (input: unknown) => ["profiles", "proposal", input] as const,
  mergePreview: (survivor: string, other: string) => ["profiles", "merge-preview", survivor, other] as const,
} as const;

export const profileOptionsQuery = queryOptions({
  queryKey: profileKeys.options,
  queryFn: ({ signal }) => apiGet("/api/profiles/options", profileOptionsResponseSchema, signal),
  staleTime: 60_000,
});

export const appearancesQuery = (id: string) =>
  queryOptions({
    queryKey: profileKeys.appearances(id),
    queryFn: ({ signal }) =>
      apiGet(`/api/profiles/${encodeURIComponent(id)}/appearances`, appearancesResponseSchema, signal),
    refetchInterval: POLL_MS,
  });

export const candidatesQuery = (q: string) =>
  queryOptions({
    queryKey: profileKeys.candidates(q),
    queryFn: ({ signal }) => apiGet(withQuery("/api/profiles/candidates", { q }), candidatesResponseSchema, signal),
    staleTime: 60_000,
  });

export const duplicatesQuery = (name: string) =>
  queryOptions({
    queryKey: profileKeys.duplicates(name),
    queryFn: ({ signal }) => apiGet(withQuery("/api/profiles/duplicates", { name }), duplicatesResponseSchema, signal),
  });

export const proposalQuery = (input: { wikidata_id?: string; url?: string }, csrf: string | null) =>
  queryOptions({
    queryKey: profileKeys.proposal(input),
    queryFn: () => apiPost("/api/profiles/proposal", input, identityProposalSchema, csrf),
    staleTime: 60_000,
    retry: false,
  });

export const mergePreviewQuery = (survivor: string, other: string) =>
  queryOptions({
    queryKey: profileKeys.mergePreview(survivor, other),
    queryFn: ({ signal }) =>
      apiGet(withQuery("/api/profiles/merge-preview", { survivor, other }), mergePreviewSchema, signal),
  });

export const profileHistoryQuery = (id: string, category: ProfileHistoryCategory) =>
  infiniteQueryOptions({
    queryKey: [...auditKeys.profile(id), category],
    queryFn: ({ pageParam, signal }) =>
      apiGet(
        withQuery(`/api/profiles/${encodeURIComponent(id)}/history`, {
          category: category === "all" ? undefined : category,
          cursor: pageParam ?? undefined,
        }),
        profileHistoryResponseSchema,
        signal,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    refetchInterval: POLL_MS,
    placeholderData: (previous) => previous,
  });

export const profileCommands = {
  create: (body: CreateProfileRequest, csrf: string | null) =>
    apiPost("/api/profiles", body, profileCreatedSchema, csrf),
  completeIdentity: (
    id: string,
    body: {
      wikidata_id: string;
      source_url: string;
      base_revision: number;
      accept: Record<string, boolean>;
      reason: string;
    },
    csrf: string | null,
  ) => apiPost(`/api/profiles/${encodeURIComponent(id)}/identity`, body, profileRevisionSchema, csrf),
  startTracking: (
    id: string,
    body: { selected: string[]; sources: string[]; history: PlanHistory; reason: string },
    csrf: string | null,
  ) => apiPost(`/api/profiles/${encodeURIComponent(id)}/start-tracking`, body, trackingStartedSchema, csrf),
  decide: (
    id: string,
    suggestion: string,
    body: { decision: "approve"; reason?: string } | { decision: "reject"; reason: string },
    csrf: string | null,
  ) =>
    apiPost(
      `/api/profiles/${encodeURIComponent(id)}/suggestions/${encodeURIComponent(suggestion)}`,
      body,
      suggestionDecidedSchema,
      csrf,
    ),
  discover: (id: string, csrf: string | null) =>
    apiPost(`/api/profiles/${encodeURIComponent(id)}/discover`, {}, discoveryStartedSchema, csrf),
  editIdentity: (id: string, body: EditIdentityRequest, csrf: string | null) =>
    apiPost(`/api/profiles/${encodeURIComponent(id)}/edit-identity`, body, profileRevisionSchema, csrf),
  refresh: (id: string, csrf: string | null) =>
    apiPost(`/api/profiles/${encodeURIComponent(id)}/refresh`, {}, refreshProposalSchema, csrf),
  decideRefresh: (
    id: string,
    proposal: string,
    body: { base_revision: number; reason: string; decisions: Record<string, "accept" | "reject"> },
    csrf: string | null,
  ) =>
    apiPost(
      `/api/profiles/${encodeURIComponent(id)}/refresh/${encodeURIComponent(proposal)}`,
      body,
      refreshDecidedSchema,
      csrf,
    ),
  retire: (id: string, body: { base_revision: number; reason: string }, csrf: string | null) =>
    apiPost(`/api/profiles/${encodeURIComponent(id)}/retire`, body, profileRevisionSchema, csrf),
  reactivate: (id: string, body: { base_revision: number; reason: string; catch_up: boolean }, csrf: string | null) =>
    apiPost(`/api/profiles/${encodeURIComponent(id)}/reactivate`, body, profileReactivatedSchema, csrf),
  deleteDraft: (id: string, body: { base_revision: number; reason: string }, csrf: string | null) =>
    apiPost(`/api/profiles/${encodeURIComponent(id)}/delete`, body, draftDeletedSchema, csrf),
  restore: (id: string, body: { reason: string }, csrf: string | null) =>
    apiPost(`/api/profiles/${encodeURIComponent(id)}/restore`, body, profileRevisionSchema, csrf),
  split: (
    id: string,
    body: { base_revision: number; name: string; names: string[]; source_ids: string[]; reason: string },
    csrf: string | null,
  ) => apiPost(`/api/profiles/${encodeURIComponent(id)}/split`, body, profileSplitSchema, csrf),
  merge: (
    body: {
      survivor_id: string;
      other_id: string;
      survivor_revision: number;
      other_revision: number;
      field_choices: MergeFieldChoices;
      reason: string;
    },
    csrf: string | null,
  ) => apiPost("/api/profiles/merges", body, mergeStartedSchema, csrf),
  reverseMerge: (merge: string, body: { reason: string }, csrf: string | null) =>
    apiPost(`/api/profiles/merges/${encodeURIComponent(merge)}/reverse`, body, mergeReversedSchema, csrf),
  previewImport: (body: { text: string }, csrf: string | null) =>
    apiPost("/api/profiles/import/preview", body, importPreviewSchema, csrf),
  importRoster: (body: { text: string; lines: number[]; languages: string[]; reason: string }, csrf: string | null) =>
    apiPost("/api/profiles/import", body, importResultSchema, csrf),
  exportRoster: (body: { scope: "shown" | "all"; ids: string[] }, csrf: string | null) =>
    apiPost("/api/profiles/export", body, exportResultSchema, csrf),
};
